import { useState } from "react";
import { Button } from "@/components/ui/button";
import PaystackPop from "@paystack/inline-js";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import useUIMisc from "hooks/useUIMisc";
import {
  checkIfAllTicketsAreFree,
  getCurrencyByCountryName,
} from "services/misc";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Info } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { mainCountries } from "utils/countries";
import { axiosPrivate } from "config/axios";
import { useNavigate } from "react-router-dom";

interface Ticket {
  _id: string;
  id: string;
  name: string;
  desc: string;
  price: number;
  maxOrder: number;
}

interface Event {
  _id: string;
  name: string;
  venue: {
    country: string;
  };
}

interface TicketBookingProps {
  tickets: Ticket[];
  event: Event;
}

interface UserInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  countryCode: string;
}

const TicketBooking = ({ tickets, event }: TicketBookingProps) => {
  const paystack = new PaystackPop();
  const navigate = useNavigate();
  const { showToast, closeLoading, showLoading } = useUIMisc();
  const [ticketQuantities, setTicketQuantities] = useState<any[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    countryCode: "+233",
  });

  const handleQuantityChange = (ticket: any, quantity: number) => {
    let allData: any = [...ticketQuantities];
    const findTicketIndex = allData.findIndex((a: any) => a._id === ticket._id);

    if (findTicketIndex !== -1 && quantity > 0) {
      allData[findTicketIndex].orderQty = quantity;
    } else if (findTicketIndex !== -1 && quantity === 0) {
      allData = allData.filter((arr: any) => arr._id !== ticket._id);
    } else {
      allData.push({ ...ticket, orderQty: quantity });
    }

    setTicketQuantities([...allData]);
  };

  const getTotalAmount = () => {
    return tickets.reduce((total, ticket) => {
      const findTicketIndex = ticketQuantities.findIndex(
        (a: any) => a._id === ticket._id
      );
      const quantity =
        findTicketIndex !== -1
          ? ticketQuantities[findTicketIndex].orderQty || 0
          : 0;
      return total + ticket.price * quantity;
    }, 0);
  };

  const getTicketQty = (_id: string) => {
    const findTicketIndex = ticketQuantities.findIndex(
      (a: any) => a._id === _id
    );
    const quantity =
      findTicketIndex !== -1
        ? ticketQuantities[findTicketIndex].orderQty || 0
        : 0;
    return quantity;
  };

  const getTotalTickets = () => {
    const totalQty = ticketQuantities.reduce(
      (sum: any, ticket) => sum + ticket.orderQty,
      0
    );
    return totalQty;
  };

  const handleGetTicket = () => {
    const totalTickets = getTotalTickets();
    if (totalTickets === 0) {
      showToast("Please select at least one ticket", "error");
      return;
    }
    setIsDialogOpen(true);
  };

  const generateReference = () => {
    const timestamp = Date.now();
    const randomStr = Math.random().toString(36).substring(2, 8); // 6-char alphanumeric
    return `ticket_${timestamp}_${randomStr}`;
  };

  const handleMakePayment = () => {
    const reference = generateReference();
    setIsDialogOpen(false);
    if (
      !userInfo.firstName ||
      !userInfo.lastName ||
      !userInfo.email ||
      !userInfo.phone
    ) {
      showToast("Please fill in all fields", "error");
      return;
    }

    const totalAmount = getTotalAmount();
    // const currency = getCurrencyByCountryName(event.venue.country);

    const amount = totalAmount * 100;
    paystack.newTransaction({
      key: process.env.REACT_APP_PS_PUBLIC_LIVE_KEY,
      email: userInfo.email,
      amount: amount + amount * 0.04,
      channels: ["card", "bank", "ussd", "qr", "mobile_money"],
      currency: "GHS",
      metadata: {
        custom_fields: [
          {
            display_name: "Full Name",
            variable_name: "full_name",
            value: `${userInfo.firstName} ${userInfo.lastName}`,
          },
          {
            display_name: "Phone Number",
            variable_name: "phone_number",
            value: `${userInfo.countryCode}${userInfo.phone}`,
          },
          {
            display_name: "Event",
            variable_name: "event_name",
            value: `${event.name}`,
          },
        ],
      },
      callback: (response: any) => {
        showToast(`Payment successful!`, "success");
        automateTicketFunc(response);
        setIsDialogOpen(false);
        // Reset form and quantities
        setTicketQuantities([]);
        setUserInfo({
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
          countryCode: "+233",
        });
      },
      onClose: () => {
        showToast("Transaction not completed!", "error");
        setIsDialogOpen(true);
      },
    });
  };

  const automateTicketFunc = async (data: any) => {
    const purchaseData = {
      subTotal: getTotalAmount().toFixed(2),
      serviceFee: (getTotalAmount() * 0.04).toFixed(2),
      total: (getTotalAmount() * 0.04 + getTotalAmount()).toFixed(2),
      transaction_info: data,
      tickets: ticketQuantities,
      origin: event.venue.country,
      soldBy: "",
    };

    const contactInfo = {
      firstname: userInfo.firstName,
      lastname: userInfo.lastName,
      phone: `${userInfo.countryCode}${userInfo.phone}`,
      email: userInfo.email,
    };

    try {
      const ticketRes = await axiosPrivate.post(
        `/tickets/guest/hemor-offline/${event._id}`,
        {
          ...contactInfo,
          ...purchaseData,
        }
      );

      const mainTicket = ticketRes.data.data;
      setTimeout(() => {
        navigate(`/offline-ticket/${mainTicket._id}`);
      }, 3000);
    } catch (error: any) {}
  };

  return (
    <>
      <div className="bg-gray-100 p-4 rounded-lg mb-6">
        <h2 className="font-semibold text-gray-800 mb-2">Tickets</h2>
        <div className="md:grid grid-cols-2 gap-4">
          {tickets && tickets.length > 0 ? (
            <>
              {tickets.map((ticket: Ticket) => (
                <div
                  key={ticket.id}
                  className="mb-4 p-3 bg-white rounded-lg border"
                >
                  <div className="flex items-center gap-2 mb-2">
                    <h6 className="text-sm text-gray-600">{ticket.name}</h6>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0"
                        >
                          <Info className="h-3 w-3 text-gray-400 hover:text-gray-600" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80">
                        <div className="space-y-3">
                          <h4 className="font-semibold text-sm">
                            {ticket.name}
                          </h4>
                          <p className="text-sm text-gray-600">{ticket.desc}</p>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    {ticket.price !== 0 ? (
                      <span className="font-bold text-md md:text-2xl text-black">
                        {getCurrencyByCountryName(event.venue.country)}{" "}
                        {ticket.price}
                      </span>
                    ) : (
                      <span className="font-bold text-md md:text-2xl text-black">
                        Free
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor={`qty-${ticket._id}`} className="text-sm">
                      Qty:
                    </Label>
                    <Select
                      value={getTicketQty(ticket._id)?.toString() || "0"}
                      onValueChange={(value) =>
                        handleQuantityChange(ticket, Number.parseInt(value))
                      }
                    >
                      <SelectTrigger className="w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: ticket.maxOrder + 1 }, (_, i) => (
                          <SelectItem key={i} value={i.toString()}>
                            {i}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-gray-500">
                      Max: {ticket.maxOrder}
                    </span>
                  </div>
                </div>
              ))}
            </>
          ) : null}
        </div>

        {getTotalTickets() > 0 && (
          <div className="mt-4 p-3 bg-white rounded-lg border space-y-2">
            <div className="flex justify-between items-center">
              <span className="font-semibold">
                Total: {getTotalTickets()} ticket(s)
              </span>
              <span className="font-bold text-lg">
                {getCurrencyByCountryName(event.venue.country)}{" "}
                {getTotalAmount()}
              </span>
            </div>
            <div className="flex justify-between items-center text-sm text-gray-600">
              <span>Service Fee (4%):</span>
              <span>
                {getCurrencyByCountryName(event.venue.country)}{" "}
                {(getTotalAmount() * 0.04).toFixed(2)}
              </span>
            </div>
            <hr className="border-gray-300" />
            <div className="flex justify-between items-center text-sm font-bold">
              <span>Total Amount:</span>
              <span>
                {getCurrencyByCountryName(event.venue.country)}{" "}
                {(getTotalAmount() + getTotalAmount() * 0.04).toFixed(2)}
              </span>
            </div>
          </div>
        )}

        <Button
          className="w-full font-bold bg-[#d60c0c] hover:bg-black text-white mt-4"
          onClick={handleGetTicket}
        >
          Get Ticket
        </Button>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Enter Your Information</DialogTitle>
          </DialogHeader>
          <Alert className="mb-4 bg-yellow-300">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Tickets would be sent to you via SMS & email, please ensure
              details are correct
            </AlertDescription>
          </Alert>
          <div className="grid grid-cols-2 gap-4 py-4 pb-0">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={userInfo.firstName}
                onChange={(e) =>
                  setUserInfo((prev) => ({
                    ...prev,
                    firstName: e.target.value,
                  }))
                }
                placeholder="Enter first name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={userInfo.lastName}
                onChange={(e) =>
                  setUserInfo((prev) => ({ ...prev, lastName: e.target.value }))
                }
                placeholder="Enter last name"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <div className="flex gap-2">
              <Select
                value={userInfo.countryCode}
                onValueChange={(value) =>
                  setUserInfo((prev) => ({ ...prev, countryCode: value }))
                }
              >
                <SelectTrigger className="w-24">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {mainCountries.map((country) => (
                    <SelectItem
                      key={country.dial_code}
                      value={country.dial_code}
                    >
                      {country.flag} {country.dial_code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                id="phone"
                value={userInfo.phone}
                onChange={(e) =>
                  setUserInfo((prev) => ({ ...prev, phone: e.target.value }))
                }
                placeholder="Phone number"
                className="flex-1"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={userInfo.email}
              onChange={(e) =>
                setUserInfo((prev) => ({ ...prev, email: e.target.value }))
              }
              placeholder="Enter email address"
            />
          </div>

          <div className="bg-gray-50 p-3 rounded-lg space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span>Total Tickets: {getTotalTickets()}</span>
              <span className="font-bold">
                {getCurrencyByCountryName(event.venue.country)}{" "}
                {getTotalAmount()}
              </span>
            </div>
            <div className="flex justify-between items-center text-sm text-gray-600">
              <span>Service Fee (4%):</span>
              <span>
                {getCurrencyByCountryName(event.venue.country)}{" "}
                {(getTotalAmount() * 0.04).toFixed(2)}
              </span>
            </div>
            <hr className="border-gray-300" />
            <div className="flex justify-between items-center text-sm font-bold">
              <span>Total Amount:</span>
              <span>
                {getCurrencyByCountryName(event.venue.country)}{" "}
                {(getTotalAmount() + getTotalAmount() * 0.04).toFixed(2)}
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() =>
                checkIfAllTicketsAreFree(ticketQuantities) === "paid"
                  ? handleMakePayment()
                  : automateTicketFunc({
                      status: "success",
                      reference: `T${Date.now()}`,
                      message: "Approved",
                    })
              }
              className="bg-[#d60c0c] hover:bg-black text-white"
            >
              Make Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default TicketBooking;
